import { addToast } from '@heroui/react';
import { useState } from 'react';

import { getKreatliPlan, KREATLI_PLANS, KreatliPlanId } from '../../../data/kreatli-plans';
import { usePlansModalVisibility } from '../../../hooks/usePlansModalVisibility';
import { trackEvent } from '../../../lib/amplitude';
import { saveCheckoutAnalyticsContext } from '../../../lib/amplitudeUser';
import { usePostUserSubscription } from '../../../services/hooks';
import { UserDto } from '../../../services/types';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import { Plan } from './Plan';
import { TrialStartPanel } from './TrialStartPanel';

const ENTERPRISE_DEMO_URL = 'https://calendar.app.google/NXbAeTAUwaBGh5x49';

const creatorPlan = getKreatliPlan('creator');
const teamPlan = getKreatliPlan('team');

const UPGRADE_PLANS = [
  {
    id: 'creator' as const,
    name: creatorPlan.name,
    description: 'For solo creators and small teams who need full review access.',
    previousPlan: creatorPlan.previousPlan,
    limits: creatorPlan.limits,
    uniqueFeatures: [{ label: 'Frame-accurate video review' }, { label: 'Guest review links and comments' }],
    price: creatorPlan.priceMonthly ?? undefined,
  },
  {
    id: 'team' as const,
    name: teamPlan.name,
    description: 'For teams that need more seats and storage on the same review workflow.',
    previousPlan: teamPlan.previousPlan,
    limits: teamPlan.limits,
    uniqueFeatures: [],
    price: teamPlan.priceMonthly ?? undefined,
  },
];

const PLAN_PRICES = Object.fromEntries(
  KREATLI_PLANS.map((plan) => [plan.id, plan.priceMonthly ?? undefined]),
) as Record<KreatliPlanId, number | undefined>;

interface Props {
  user: UserDto;
}

export const PlansForm = ({ user }: Props) => {
  const trialAvailable = !user.subscription.hasUsedTrial;
  const [selectedPlan, setSelectedPlan] = useState<'creator' | 'team' | 'enterprise' | null>(
    trialAvailable ? null : (user?.subscription.plan ?? null),
  );

  const plansModalEntry = usePlansModalVisibility((state) => state.plansModalEntry);
  const { mutate: upgradePlan, isPending: isUpgradingPlan } = usePostUserSubscription();

  const handleUpgradePlan = (plan: 'creator' | 'team' | 'enterprise') => {
    if (!plan) return;

    const checkoutContext = {
      plan_id: plan,
      plans_modal_entry: plansModalEntry,
      plan_price_usd: PLAN_PRICES[plan],
    };

    trackEvent('trial_checkout_started', {
      plan_id: plan,
      plans_modal_entry: plansModalEntry,
      plan_price_usd: checkoutContext.plan_price_usd,
    });

    saveCheckoutAnalyticsContext(checkoutContext);

    upgradePlan(
      {
        requestBody: { plan },
      },
      {
        onSuccess: ({ url }) => {
          trackEvent('trial_checkout_redirect', {
            plan_id: plan,
            plans_modal_entry: plansModalEntry,
            plan_price_usd: checkoutContext.plan_price_usd,
          });
          location.href = url;
        },
        onError: (error) => {
          trackEvent('trial_checkout_failed', {
            plan_id: plan,
            plans_modal_entry: plansModalEntry,
            error_message: getErrorMessage(error).slice(0, 200),
          });
          addToast({ title: getErrorMessage(error), color: 'danger', variant: 'flat' });
        },
      },
    );
  };

  const handleSelectPlan = (plan: 'creator' | 'team') => () => {
    setSelectedPlan(plan);
    handleUpgradePlan(plan);
  };

  if (trialAvailable) {
    return (
      <TrialStartPanel
        isCreatorLoading={isUpgradingPlan && selectedPlan === 'creator'}
        isTeamLoading={isUpgradingPlan && selectedPlan === 'team'}
        onStartCreator={() => {
          setSelectedPlan('creator');
          handleUpgradePlan('creator');
        }}
        onStartTeam={() => {
          setSelectedPlan('team');
          handleUpgradePlan('team');
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 md:flex-row">
        {UPGRADE_PLANS.map(({ id, name, description, price, previousPlan, limits, uniqueFeatures }) => (
          <Plan
            key={id}
            isSelected={selectedPlan === id}
            isLoading={isUpgradingPlan}
            isCurrent={user.subscription.plan === id && user.subscription.isActive}
            isCurrentTrial={user.subscription.plan === id && user.subscription.isTrial}
            name={name}
            isTrialAvailable={false}
            description={description}
            price={price}
            previousPlan={previousPlan}
            limits={limits}
            uniqueFeatures={uniqueFeatures}
            onClick={handleSelectPlan(id)}
          />
        ))}
      </div>
      <a
        href={ENTERPRISE_DEMO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-center text-sm text-foreground-500 underline-offset-2 hover:text-foreground hover:underline"
      >
        Enterprise? Book a demo
      </a>
    </div>
  );
};
